// Enhanced API client with retry logic and rate limiting handling

import RetryUtils from './retryUtils';

interface ApiClientOptions {
  baseURL: string;
  timeout?: number;
  retryOptions?: {
    maxRetries?: number;
    baseDelay?: number;
    maxDelay?: number;
  };
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  cached?: boolean;
}

class ApiClient {
  private baseURL: string;
  private timeout: number;
  private retryOptions: any;
  private stateRequestThrottle: Map<string, number> = new Map();

  constructor(options: ApiClientOptions) {
    this.baseURL = options.baseURL;
    this.timeout = options.timeout || 30000;
    this.retryOptions = {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 10000,
      ...options.retryOptions
    };
  }

  /**
   * Make a GET request with retry logic
   */
  async get<T = any>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'GET'
    });
  }

  /**
   * Make a POST request with retry logic
   */
  async post<T = any>(endpoint: string, data?: any, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * Make a PUT request with retry logic
   */
  async put<T = any>(endpoint: string, data?: any, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * Make a DELETE request with retry logic
   */
  async delete<T = any>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE'
    });
  }

  /**
   * Core request method with retry logic
   */
  private async request<T = any>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    
    // Add authentication token if available
    const token = localStorage.getItem('cad_token');
    if (token) {
      options.headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      };
    }

    try {
      const result = await RetryUtils.executeWithRetry(async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        try {
          const response = await fetch(url, {
            ...options,
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          // Handle rate limiting with custom retry delay
          if (response.status === 429) {
            const retryAfter = RetryUtils.getRetryAfterDelay(response);
            if (retryAfter > 0) {
              await new Promise(resolve => setTimeout(resolve, retryAfter));
            }
          }

          if (!response.ok) {
            const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
            (error as any).response = response;
            throw error;
          }

          const data = await response.json();
          return data;
        } catch (error) {
          clearTimeout(timeoutId);
          throw error;
        }
      }, this.retryOptions);

      if (result.success) {
        return result.data as ApiResponse<T>;
      } else {
        return {
          success: false,
          error: RetryUtils.getRateLimitMessage(result.error)
        };
      }
    } catch (error) {
      console.error(`[ApiClient] Request failed for ${endpoint}:`, error);
      return {
        success: false,
        error: RetryUtils.getRateLimitMessage(error)
      };
    }
  }

  /**
   * Get configuration data with caching
   */
  async getConfig(): Promise<ApiResponse> {
    return this.get('/config');
  }

  /**
   * Get POIs with caching
   */
  async getPOIs(): Promise<ApiResponse> {
    return this.get('/config/pois');
  }

  /**
   * Get call codes with caching
   */
  async getCallCodes(): Promise<ApiResponse> {
    return this.get('/config/call-codes');
  }

  /**
   * Get all calls
   */
  async getCalls(): Promise<ApiResponse> {
    return this.get('/calls');
  }

  /**
   * Get active calls
   */
  async getActiveCalls(): Promise<ApiResponse> {
    return this.get('/calls/active');
  }

  /**
   * Get all units
   */
  async getUnits(): Promise<ApiResponse> {
    return this.get('/units');
  }

  /**
   * Get available units
   */
  async getAvailableUnits(): Promise<ApiResponse> {
    return this.get('/units/available');
  }

  /**
   * Update unit status
   */
  async updateUnitStatus(unitId: string, status: string): Promise<ApiResponse> {
    return this.post('/units/status', { unitId, status });
  }

  /**
   * Create a new call
   */
  async createCall(callData: any): Promise<ApiResponse> {
    return this.post('/calls', callData);
  }

  /**
   * Update a call
   */
  async updateCall(callId: string, updates: any): Promise<ApiResponse> {
    return this.put(`/calls/${callId}`, updates);
  }

  /**
   * Close a call
   */
  async closeCall(callId: string): Promise<ApiResponse> {
    return this.post(`/calls/${callId}/close`);
  }

  /**
   * End/close a call from Roblox server
   */
  async endRobloxCall(callID: string, timeCreated?: string): Promise<ApiResponse> {
    return this.post('/endRobloxCall', {
      callID,
      timeCreated
    });
  }

  /**
   * Assign unit to call
   */
  async assignUnitToCall(callId: string, unitId: string): Promise<ApiResponse> {
    return this.post(`/calls/${callId}/assign/${unitId}`);
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<ApiResponse> {
    return this.get('/monitoring/cache');
  }

  /**
   * Get rate limiting statistics
   */
  async getRateLimitStats(): Promise<ApiResponse> {
    return this.get('/monitoring/rate-limits');
  }

  /**
   * Get current state for resync with throttling
   */
  async getState(): Promise<ApiResponse> {
    const now = Date.now();
    const lastRequest = this.stateRequestThrottle.get('/state') || 0;
    const timeSinceLastRequest = now - lastRequest;
    
    // Throttle state requests to once per 5 seconds
    if (timeSinceLastRequest < 5000) {
      console.log('[ApiClient] State request throttled, too soon since last request');
      return {
        success: false,
        error: 'State request throttled'
      };
    }
    
    this.stateRequestThrottle.set('/state', now);
    return this.get('/state');
  }
}

// Create default instance
const apiClient = new ApiClient({
  baseURL: import.meta.env.VITE_API_BASE || 'http://localhost:5000/api',
  retryOptions: {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000
  }
});

export default apiClient;
export { ApiClient };

